import { useRef } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gem, Radio, MapPin, Sparkles, User as UserIcon, Settings, Coins, ChevronLeft } from 'lucide-react';
import { useSession } from '../lib/session.jsx';
import { useFieldAlertActions } from '../lib/useFieldAlerts.js';
import { useCrystal } from '../lib/crystalState.jsx';
import { cn } from '../lib/cn.js';
import Aurora from './Aurora.jsx';
import { CrystalGlyph } from './Crystal.jsx';
import { growthFor } from '../lib/crystal.js';

const NAV = [
  { label: 'Sanctum',    to: '/',             icon: Gem },
  { label: 'Map',        to: '/map',          icon: MapPin },
  { label: 'Ceremonies', to: '/ceremonies',   icon: Radio },
  { label: 'Covenant',   to: '/subscription', icon: Sparkles },
  { label: 'You',        to: '/profile',      icon: UserIcon },
];

export default function Layout({ children }) {
  const { tokens } = useSession();
  useFieldAlertActions();
  const loc = useLocation();
  const nav = useNavigate();
  const onMap = loc.pathname === '/map';
  const atHome = loc.pathname === '/';
  const back = () => (window.history.length > 1 ? nav(-1) : nav('/'));

  // Swipe between tabs. Skipped on the map (it owns its gestures) and when
  // the touch starts in a control that scrolls or edits horizontally.
  const touch = useRef(null);
  const onTouchStart = (e) => {
    if (onMap || e.target.closest('[data-noswipe], input, textarea, select, .overflow-x-auto')) { touch.current = null; return; }
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, at: Date.now() };
  };
  const onTouchEnd = (e) => {
    const s = touch.current; touch.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) < 72 || Math.abs(dy) > 48 || Date.now() - s.at > 600) return;
    const i = NAV.findIndex((n) => n.to === loc.pathname);
    const next = i < 0 ? null : NAV[i + (dx < 0 ? 1 : -1)];
    if (next) nav(next.to);
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <Aurora />

      {/* Desktop rail */}
      <aside className="hidden md:flex md:w-[268px] shrink-0 flex-col justify-between p-5 sticky top-0 h-screen z-10">
        <div className="glass p-4 flex-1 flex flex-col">
          <Link to="/" className="px-2 pt-1"><Mark /></Link>
          <nav className="mt-8 space-y-1">
            {NAV.map(({ label, to, icon: Icon }) => (
              <NavLink key={to} to={to} end={to === '/'}
                className={({ isActive }) => cn(
                  'relative flex items-center gap-3 px-3 py-2.5 rounded-2xl text-[0.92rem] font-medium transition-colors',
                  isActive ? 'text-text' : 'text-text-3 hover:text-text-2'
                )}>
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span layoutId="rail-pill" className="absolute inset-0 rounded-2xl"
                        style={{ background: 'var(--ground-2)' }}
                        transition={{ type: 'spring', stiffness: 400, damping: 34 }} />
                    )}
                    <Icon size={17} className="relative" style={{ color: isActive ? 'var(--clay)' : 'currentColor' }} />
                    <span className="relative">{label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto space-y-3">
            <CrystalCard />
            <div className="flex items-center justify-between px-2">
              <Link to="/subscription?tab=tokens" className="text-sm font-semibold nums inline-flex items-center gap-1.5" style={{ color: 'var(--gold)' }}>
                <Coins size={14} /> {tokens} <span className="eyebrow" style={{ letterSpacing: '0.1em' }}>tokens</span>
              </Link>
              <Link to="/settings" aria-label="Settings"
                    className="p-2 rounded-xl transition-colors hover:bg-[var(--glass-2)]"
                    style={{ color: loc.pathname === '/settings' ? 'var(--clay)' : 'var(--text-3)' }}>
                <Settings size={15} />
              </Link>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile header — the map draws its own overlay instead */}
      {!onMap && (
        <header className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-16"
                style={{ paddingTop: 'env(safe-area-inset-top)', boxSizing: 'content-box',
                         background: 'linear-gradient(var(--ground), color-mix(in oklab, var(--ground) 0%, transparent))' }}>
          <div className="flex items-center gap-1.5">
            {!atHome && (
              <button onClick={back} aria-label="Back" className="glass-flat pill p-2" style={{ color: 'var(--text-2)' }}>
                <ChevronLeft size={16} />
              </button>
            )}
            <Link to="/" className="pl-1"><Mark compact /></Link>
          </div>
          <div className="flex items-center gap-2">
            <TokenPill tokens={tokens} />
            <Link to="/settings" aria-label="Settings" className="glass-flat pill p-2" style={{ color: 'var(--text-2)' }}>
              <Settings size={14} />
            </Link>
          </div>
        </header>
      )}

      <main className={cn('relative z-[1] flex-1 min-w-0 px-5 md:px-10 md:py-8 md:pb-16 max-w-[1100px]',
                          onMap ? 'pb-0' : 'pb-[calc(var(--tabbar-h)+env(safe-area-inset-bottom)+1.5rem)]')}>
        {children}
      </main>

      {/* Mobile tab bar, docked to the bottom edge */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40"
           style={{ paddingBottom: 'env(safe-area-inset-bottom)',
                    background: 'color-mix(in oklab, var(--ground) 86%, transparent)', backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)', borderTop: '1px solid var(--edge)' }}>
        <div className="grid grid-cols-5 px-2 pt-1.5" style={{ height: 'var(--tabbar-h)' }}>
          {NAV.map(({ label, to, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === '/'}
              className="relative flex flex-col items-center justify-center gap-0.5 rounded-2xl">
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span layoutId="tab-pill" className="absolute inset-x-1 inset-y-1 rounded-2xl"
                      style={{ background: 'var(--ground-2)' }}
                      transition={{ type: 'spring', stiffness: 400, damping: 34 }} />
                  )}
                  <Icon size={18} className="relative" style={{ color: isActive ? 'var(--clay)' : 'var(--text-3)' }} />
                  <span className="relative text-[0.6rem] font-semibold tracking-wide"
                        style={{ color: isActive ? 'var(--text)' : 'var(--text-3)' }}>
                    {label}
                  </span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

/** Balance pill; tapping it goes to the token offerings. */
export function TokenPill({ tokens, className }) {
  return (
    <Link to="/subscription?tab=tokens" aria-label={`${tokens} tokens — get more`}
          className={cn('glass-flat pill px-3 py-1.5 text-xs font-semibold nums inline-flex items-center gap-1.5', className)}
          style={{ color: 'var(--gold)' }}>
      <Coins size={13} /> {tokens}
    </Link>
  );
}

function CrystalCard() {
  const { info, hue, state, loaded } = useCrystal();
  if (!loaded) return null;
  return (
    <Link to="/" className="glass-flat flex items-center gap-3 p-3 pr-4 glass-hover">
      <div className="shrink-0 -my-2">
        <CrystalGlyph hue={hue} size={44} growth={growthFor(state.xp)} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[0.82rem] font-semibold leading-tight">{info.name}</p>
        <p className="text-[0.68rem] mt-0.5" style={{ color: 'var(--text-3)' }}>Level {info.level}</p>
        <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ background: 'var(--glass-2)' }}>
          <div className="h-full rounded-full" style={{ width: `${info.progress * 100}%`,
               background: `hsl(${hue} 45% 58%)` }} />
        </div>
      </div>
    </Link>
  );
}

export function Mark({ compact, light }) {
  return (
    <div className="flex items-center gap-2.5">
      <svg width={compact ? 22 : 26} height={compact ? 22 : 26} viewBox="0 0 24 24" aria-hidden>
        <defs>
          <linearGradient id="mark-g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="var(--apricot)" /><stop offset="1" stopColor="var(--clay)" />
          </linearGradient>
        </defs>
        <path d="M12 2 L17 9 L12 22 L7 9 Z" fill="url(#mark-g)" />
        <path d="M12 2 L12 22 M7 9 L17 9" stroke="#fff" strokeOpacity="0.5" strokeWidth="0.8" />
      </svg>
      <span className={cn('display tracking-tight', compact ? 'text-[1.35rem]' : 'text-[1.6rem]')}
            style={{ color: light ? '#fff' : 'var(--text)' }}>AuraFlow</span>
    </div>
  );
}
